import { logger } from '@automata/utils';
import { createSelector } from '@reduxjs/toolkit';
import mapValues from 'lodash/mapValues';

import { isNotNullish } from '../components/PreviewPanel/utils';
import type { TaskDependency } from '../features/graph/graphSlice';
import { selectTaskDependencies } from '../features/graph/selectors';
import { selectLabwareIdsForTaskByTaskId } from '../features/selectors/selectLabwareIdsForTaskByTaskId';
import {
  selectActionTaskPropertiesStrict,
  selectInstrumentDataById,
  selectLabwareProperties,
  selectTaskPropertiesStrict,
} from '../features/workflow/selectors';
import { useAppSelector } from '../hooks';
import type { RootState } from '../store';
import {
  AllTaskProperties,
  InstrumentForLocation,
  LabwareProperties,
  TaskProperties,
} from '../types';

export type Task = AllTaskProperties & {
  labware: LabwareProperties[] | null;
  instrument: InstrumentForLocation | null;
  dependencies: TaskDependency[];
  dependents: TaskDependency[];
};

export type ActionTask = TaskProperties & {
  labware: LabwareProperties[];
  instrument: InstrumentForLocation | null;
  dependencies: TaskDependency[];
  dependents: TaskDependency[];
};

// TODO(IL, 2025-01-23): this selector should go in features/selectors
const createEnrichedTaskSelector = <T extends AllTaskProperties>(
  taskPropertiesSelector: (state: RootState) => Record<string, T>
) => {
  return createSelector(
    taskPropertiesSelector,
    selectLabwareIdsForTaskByTaskId,
    selectInstrumentDataById,
    selectLabwareProperties,
    selectTaskDependencies,
    (
      taskPropertiesById,
      handlesToLabwareByTaskId,
      instrumentsById,
      labwareById,
      taskDependencies
    ) => {
      return mapValues(taskPropertiesById, (task) => {
        let instrument: InstrumentForLocation | null = null;

        if (task.propertiesType === 'action') {
          instrument = instrumentsById[task.instrumentId || ''] ?? null;
        }

        const handlesToLabware = handlesToLabwareByTaskId[task.id];

        const labwareIds = Object.values(handlesToLabware ?? {}).filter(
          (id): id is string => typeof id === 'string'
        );

        const labware = labwareIds
          .map((id) => labwareById[id])
          .filter(isNotNullish);

        const dependencies = Object.values(taskDependencies).filter(
          (dep) => dep.toTaskId === task.id
        );

        const dependents = Object.values(taskDependencies).filter(
          (dep) => dep.fromTaskId === task.id
        );

        return {
          ...task,
          instrument,
          labware,
          dependencies,
          dependents,
        };
      });
    }
  );
};

const selectTasks = createEnrichedTaskSelector(selectTaskPropertiesStrict);
const selectActionTasks = createEnrichedTaskSelector(
  selectActionTaskPropertiesStrict
);

export const useAllTasks = (): Record<string, Task> => {
  return useAppSelector(selectTasks);
};

export const useTask = (taskId: string): Task => {
  const task = useAppSelector(selectTasks)[taskId];
  if (!task) {
    logger.error('Task not found', { taskId });
    throw new Error('Task not found');
  }
  return task;
};

export const useActionTask = (taskId: string): ActionTask => {
  const task = useAppSelector(selectActionTasks)[taskId];
  if (!task) {
    logger.error('Task not found', { taskId });
    throw new Error('Task not found');
  }
  return task;
};
